// Hooks for the unified approvals queue (Phase 1G). Routes are admin OR any
// approver role on the BFF; this module makes no further role checks.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export type ApprovalKind = "rule" | "workflow" | "ioc_bulk";
export type ApprovalStatus = "pending" | "approved" | "rejected" | "cancelled";

export interface Approval {
    id: string;
    kind: ApprovalKind;
    resourceType: string;
    resourceId: string;
    version?: number | null;
    requestedBy: string;
    requestedByEmail?: string;
    requestedAt: string;
    title: string;
    summary: string;
    payload: Record<string, unknown>;
    approversRequired: number;
    status: ApprovalStatus;
    decidedAt?: string | null;
    decisionNote?: string;
    approveCount: number;
    rejectCount: number;
}

export interface ApprovalDecision {
    id: string;
    approvalId: string;
    approverUserId: string;
    approverEmail?: string;
    decidedAt: string;
    decision: "approve" | "reject";
    note?: string;
}

export interface ApprovalCounts {
    pending: number;
    approved24h: number;
    rejected24h: number;
    myPending: number;
}

export interface ApprovalFilters {
    kind?: ApprovalKind | "";
    status?: ApprovalStatus | "";
    requestedBy?: string;
    q?: string;
    limit?: number;
    offset?: number;
}

function toQuery(f: ApprovalFilters): string {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) {
        if (v !== undefined && v !== null && v !== "") {
            params.set(k, String(v));
        }
    }
    const s = params.toString();
    return s ? `?${s}` : "";
}

const KEY = ["admin", "approvals"] as const;

export function useApprovals(filters: ApprovalFilters) {
    return useQuery({
        queryKey: [...KEY, "list", filters],
        queryFn: () =>
            api
                .get<{ approvals: Approval[] }>(`/api/v2/approvals${toQuery(filters)}`)
                .then((d) => d.approvals),
        staleTime: 5_000,
        refetchInterval: 15_000,
    });
}

export function useApprovalCounts() {
    return useQuery({
        queryKey: [...KEY, "counts"] as const,
        queryFn: () => api.get<ApprovalCounts>("/api/v2/approvals/counts"),
        staleTime: 5_000,
        refetchInterval: 15_000,
    });
}

export function useApproval(id: string | null) {
    return useQuery({
        queryKey: [...KEY, "detail", id],
        queryFn: () =>
            api.get<{ approval: Approval; decisions: ApprovalDecision[] }>(`/api/v2/approvals/${id}`),
        enabled: !!id,
        staleTime: 2_000,
    });
}

export function useDecideApproval() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, approve, note }: { id: string; approve: boolean; note?: string }) =>
            api.post<{ approval: Approval }>(
                `/api/v2/approvals/${id}/${approve ? "approve" : "reject"}`,
                { note: note ?? "" },
            ),
        onSuccess: (_d, vars) => {
            void qc.invalidateQueries({ queryKey: KEY });
            void qc.invalidateQueries({ queryKey: [...KEY, "detail", vars.id] });
        },
    });
}

export function useCancelApproval() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, note }: { id: string; note?: string }) =>
            api.post<{ approval: Approval }>(`/api/v2/approvals/${id}/cancel`, { note: note ?? "" }),
        onSuccess: (_d, vars) => {
            void qc.invalidateQueries({ queryKey: KEY });
            void qc.invalidateQueries({ queryKey: [...KEY, "detail", vars.id] });
        },
    });
}
